import { apiRequest, authStorage } from './api';

/** Corps KPI sous `data`, ou objet déjà plat ; jamais un tableau. */
function unwrapKpiPayload(response: unknown): Record<string, unknown> {
  if (response == null || typeof response !== 'object') {
    return {};
  }
  const r = response as Record<string, unknown>;
  const inner = r.data;
  if (inner != null && typeof inner === 'object' && !Array.isArray(inner)) {
    return inner as Record<string, unknown>;
  }
  if ('gmv' in r || 'userCount' in r || 'orderCount' in r) {
    return r;
  }
  return {};
}

export async function getAdminDashboardKpis() {
  const response = await apiRequest('/admin/dashboard/kpis', { method: 'GET', withAuth: true });
  return unwrapKpiPayload(response);
}

export async function getAdminDashboardTransactions(limit = 20) {
  const response = await apiRequest(`/admin/dashboard/transactions?limit=${limit}`, { method: 'GET', withAuth: true });
  return Array.isArray(response.data) ? response.data : [];
}

export async function getAdminDashboardLogistics() {
  const response = await apiRequest('/admin/dashboard/logistics', { method: 'GET', withAuth: true });
  return response.data;
}

export async function getAdminDashboardAlerts() {
  const response = await apiRequest('/admin/dashboard/alerts', { method: 'GET', withAuth: true });
  return response.data;
}

export async function getAdminNotificationMonitoring(hours = 24) {
  const safeHours = Number.isFinite(Number(hours)) ? Number(hours) : 24;
  const response = await apiRequest(`/admin/dashboard/notifications/monitoring?hours=${encodeURIComponent(String(safeHours))}`, {
    method: 'GET',
    withAuth: true,
  });
  return response.data;
}

export async function getAdminNotificationMonitoringThresholds() {
  const response = await apiRequest('/admin/dashboard/notifications/thresholds', {
    method: 'GET',
    withAuth: true,
  });
  return response.data;
}

export async function patchAdminNotificationMonitoringThresholds(payload) {
  const response = await apiRequest('/admin/dashboard/notifications/thresholds', {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function getAdminWithdrawals(status, extraParams = {}) {
  const params = new URLSearchParams();
  if (status) {
    params.set('status', String(status));
  }
  Object.entries(extraParams || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      params.set(key, String(value));
    }
  });
  const qs = params.toString();
  const response = await apiRequest(`/admin/withdrawals${qs ? `?${qs}` : ''}`, { method: 'GET', withAuth: true });
  return Array.isArray(response.data) ? response.data : [];
}

export async function getAdminWithdrawalsWithMeta(status, extraParams = {}) {
  const params = new URLSearchParams();
  if (status) {
    params.set('status', String(status));
  }
  Object.entries(extraParams || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      params.set(key, String(value));
    }
  });
  const qs = params.toString();
  const response = await apiRequest(`/admin/withdrawals${qs ? `?${qs}` : ''}`, { method: 'GET', withAuth: true });
  const items = Array.isArray(response.data) ? response.data : [];
  const pagination = response?.pagination && typeof response.pagination === 'object'
    ? response.pagination
    : {
      page: 1,
      limit: items.length || 1,
      total: items.length,
      totalPages: 1,
    };
  return { items, pagination };
}

export async function updateAdminWithdrawalStatus(withdrawalId, statut) {
  const response = await apiRequest(`/admin/withdrawals/${withdrawalId}/status`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify({ statut }),
  });
  return response.data;
}

export async function bulkUpdateAdminWithdrawalStatus(ids, statut) {
  const response = await apiRequest('/admin/withdrawals/bulk-status', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify({ ids, statut }),
  });
  return response.data;
}

export function buildAdminWithdrawalsExportUrl(status, extraParams = {}) {
  const params = new URLSearchParams();
  if (status) {
    params.set('status', String(status));
  }
  Object.entries(extraParams || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      params.set(key, String(value));
    }
  });
  const qs = params.toString();
  return `/admin/withdrawals/export.csv${qs ? `?${qs}` : ''}`;
}

export async function downloadAdminWithdrawalsCsv(status, extraParams = {}) {
  const endpoint = buildAdminWithdrawalsExportUrl(status, extraParams);
  const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:3000/api').replace(/\/$/, '');
  const accessToken = authStorage.getAccessToken();
  const headers = new Headers();
  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }
  const response = await fetch(`${baseUrl}${endpoint}`, {
    method: 'GET',
    headers,
  });
  if (!response.ok) {
    let message = 'Export CSV impossible';
    try {
      const payload = await response.json();
      message = payload?.message || message;
    } catch {
      // no-op
    }
    throw new Error(message);
  }
  return response.blob();
}

export async function getBusinessSettings() {
  const response = await apiRequest('/admin/settings/business', { method: 'GET', withAuth: true });
  return response.data;
}

export async function getAdminSystemSettings() {
  const response = await apiRequest('/admin/system', { method: 'GET', withAuth: true });
  return response.data;
}

export async function patchAdminSystemPlatform(payload: Record<string, unknown>) {
  const response = await apiRequest('/admin/system/platform', {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function postAdminPlatformLogo(file: File) {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiRequest('/admin/system/platform/logo', {
    method: 'POST',
    withAuth: true,
    body: formData,
  });
  return response.data;
}

export async function patchAdminSystemLegal(type: 'CGU' | 'CONFIDENTIALITE', content: string) {
  const response = await apiRequest('/admin/system/legal', {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify({ type, content }),
  });
  return response.data;
}

export async function updateCurrencySettings(payload) {
  const response = await apiRequest('/admin/settings/currency', {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function updateDeliveryPricingSettings(payload) {
  const response = await apiRequest('/admin/settings/delivery-pricing', {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function updateCommissionSettings(payload) {
  const response = await apiRequest('/admin/settings/commissions', {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function getAdminDrivers() {
  const response = await apiRequest('/admin/drivers', { method: 'GET', withAuth: true });
  return Array.isArray(response.data) ? response.data : [];
}

export async function getAdminSellers() {
  const response = await apiRequest('/admin/sellers', { method: 'GET', withAuth: true });
  if (!Array.isArray(response.data)) {
    return [];
  }
  return response.data.map((row) => ({
    ...row,
    // Compat: certains payloads exposent `logoUrl` au lieu de `urlLogo`.
    urlLogo: row?.urlLogo ?? row?.logoUrl ?? null,
    // Alignement Annuaire: mêmes clés de fallback image.
    avatarUrl: row?.avatarUrl ?? row?.urlLogo ?? row?.logoUrl ?? null,
    photoUrl: row?.photoUrl ?? row?.proprietaire?.photoUrl ?? null,
    boutiqueLogoUrl: row?.boutiqueLogoUrl ?? row?.urlLogo ?? row?.logoUrl ?? null,
  }));
}

/** Vue exhaustive admin : produits, pub, promos, commandes, livraisons, stock, réclamations, finances vendeur. */
export async function getAdminBoutiqueOverview(boutiqueId) {
  const response = await apiRequest(`/admin/boutiques/${encodeURIComponent(String(boutiqueId ?? '').trim())}/overview`, {
    method: 'GET',
    withAuth: true,
  });
  return response?.data ?? null;
}

/** Annuaire admin : utilisateurs par rôle (données base). */
export async function getAdminDirectoryUsers(role: 'CLIENT' | 'VENDEUR' | 'LIVREUR') {
  const response = await apiRequest(`/admin/directory/users?role=${encodeURIComponent(role)}`, {
    method: 'GET',
    withAuth: true,
  });
  return Array.isArray(response.data) ? response.data : [];
}

export async function getAdminDirectoryUserHistory(userId: string) {
  const response = await apiRequest(`/admin/directory/users/${encodeURIComponent(userId)}/history`, {
    method: 'GET',
    withAuth: true,
  });
  return response.data;
}

/** Détail annuaire : avis clients sur les livraisons (rôle livreur uniquement). */
export async function getAdminDirectoryLivreurReviews(userId: string, take = 80) {
  const qs = Number.isFinite(take) && take > 0 ? `?take=${encodeURIComponent(String(Math.min(take, 200)))}` : '';
  const response = await apiRequest(
    `/admin/directory/users/${encodeURIComponent(userId)}/livreur-reviews${qs}`,
    {
      method: 'GET',
      withAuth: true,
    },
  );
  return Array.isArray(response.data) ? response.data : [];
}

export async function patchAdminDirectoryUserAccount(userId: string, estCompteActif: boolean) {
  const response = await apiRequest(`/admin/directory/users/${encodeURIComponent(userId)}/account`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify({ estCompteActif }),
  });
  return response.data;
}

/** Annuaire : suppression définitive (POST explicite côté API — évite « Route non trouvée » si DELETE sur router imbriqué ou proxy). */
export async function deleteAdminDirectoryUser(userId: string) {
  const response = await apiRequest(`/admin/directory/users/${encodeURIComponent(userId)}/delete`, {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify({}),
  });
  return response.data;
}

export async function updateAdminSellerStatus(sellerId, payload) {
  const response = await apiRequest(`/admin/sellers/${sellerId}/status`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function estimateDeliveryPricing(payload) {
  const response = await apiRequest('/commandes/estimate-delivery', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function getDeliveryZones() {
  const response = await apiRequest('/admin/references/zones', { method: 'GET', withAuth: true });
  return Array.isArray(response.data) ? response.data : [];
}

export async function getProductCategories() {
  const response = await apiRequest('/admin/references/categories', { method: 'GET', withAuth: true });
  return Array.isArray(response.data) ? response.data : [];
}

export async function createDeliveryZone(payload) {
  const response = await apiRequest('/admin/references/zones', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function updateDeliveryZone(zoneId, payload) {
  const response = await apiRequest(`/admin/references/zones/${zoneId}`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function toggleDeliveryZone(zoneId, estActive) {
  const response = await apiRequest(`/admin/references/zones/${zoneId}/toggle`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify({ estActive }),
  });
  return response.data;
}

export async function createProductCategory(payload) {
  const response = await apiRequest('/admin/references/categories', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function updateProductCategory(categoryId, payload) {
  const response = await apiRequest(`/admin/references/categories/${categoryId}`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function toggleProductCategory(categoryId, estActive) {
  const response = await apiRequest(`/admin/references/categories/${categoryId}/toggle`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify({ estActive }),
  });
  return response.data;
}

export async function getAdminPartners(period = 'current_month') {
  const response = await apiRequest(`/admin/partners?period=${encodeURIComponent(period)}`, { method: 'GET', withAuth: true });
  return Array.isArray(response.data) ? response.data : [];
}

export async function createAdminPartner(payload) {
  const response = await apiRequest('/admin/partners', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function uploadAdminPartnerContract(file: File) {
  const formData = new FormData();
  formData.append('file', file);
  try {
    const adminResponse = await apiRequest('/admin/partners/upload-contract', {
      method: 'POST',
      withAuth: true,
      body: formData,
    });
    return adminResponse.data;
  } catch (error) {
    const message = error instanceof Error ? error.message : '';
    const shouldFallback = message.toLowerCase().includes('route non trouv');
    if (!shouldFallback) {
      throw error;
    }

    const fallbackFormData = new FormData();
    fallbackFormData.append('file', file);
    const authResponse = await apiRequest('/auth/upload-registration-document', {
      method: 'POST',
      withAuth: false,
      body: fallbackFormData,
    });
    return authResponse.data;
  }
}

export async function updateAdminPartner(partnerId, payload) {
  const response = await apiRequest(`/admin/partners/${encodeURIComponent(partnerId)}`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function toggleAdminPartner(partnerId, estActif) {
  const response = await apiRequest(`/admin/partners/${encodeURIComponent(partnerId)}/toggle`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify({ estActif }),
  });
  return response.data;
}

export async function getAdminSubscriptionPlans() {
  const response = await apiRequest('/admin/subscriptions/plans', { method: 'GET', withAuth: true });
  return Array.isArray(response.data) ? response.data : [];
}

export async function patchAdminSubscriptionPlan(planId: string, payload: Record<string, unknown>) {
  const response = await apiRequest(`/admin/subscriptions/plans/${encodeURIComponent(planId)}`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function getAdminOrders() {
  const response = await apiRequest('/admin/orders', { method: 'GET', withAuth: true });
  return Array.isArray(response.data) ? response.data : [];
}

export async function getAssignableDriversForOrder(orderId: string) {
  const response = await apiRequest(`/admin/orders/${encodeURIComponent(orderId)}/drivers`, {
    method: 'GET',
    withAuth: true,
  });
  return response.data;
}

export async function assignDriverToAdminOrder(orderId: string, driverUserId: string) {
  const response = await apiRequest(`/admin/orders/${encodeURIComponent(orderId)}/assign-driver`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify({ driverUserId }),
  });
  return response.data;
}

export async function getAdminReclamations(params = {}) {
  const query = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      query.set(key, String(value));
    }
  });
  const qs = query.toString();
  const response = await apiRequest(`/admin/reclamations${qs ? `?${qs}` : ''}`, { method: 'GET', withAuth: true });
  const items = Array.isArray(response.data) ? response.data : [];
  const pagination = response?.pagination && typeof response.pagination === 'object'
    ? response.pagination
    : {
      page: 1,
      limit: items.length || 1,
      total: items.length,
      totalPages: 1,
    };
  return { items, pagination };
}

export async function getAdminReclamationDetail(reclamationId: string) {
  const response = await apiRequest(`/admin/reclamations/${encodeURIComponent(reclamationId)}`, {
    method: 'GET',
    withAuth: true,
  });
  return response.data;
}

export async function updateAdminReclamationStatus(reclamationId: string, statutTraitement: string) {
  const response = await apiRequest(`/admin/reclamations/${encodeURIComponent(reclamationId)}/statut`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify({ statutTraitement }),
  });
  return response.data;
}

export async function updateAdminReclamationResolution(reclamationId: string, resolution: string) {
  const response = await apiRequest(`/admin/reclamations/${encodeURIComponent(reclamationId)}/resolution`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify({ resolution }),
  });
  return response.data;
}

/** F12.1 — packs tarifaires (admin). */
export async function getAdminPublicityPacks() {
  const response = await apiRequest('/admin/packs-publicitaires', { method: 'GET', withAuth: true });
  return Array.isArray(response.data) ? response.data : [];
}

export async function patchAdminPublicityPack(packId: string, payload: Record<string, unknown>) {
  const response = await apiRequest(`/admin/packs-publicitaires/${encodeURIComponent(packId)}`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

/** F12 — liste des campagnes publicitaires vendeurs (supervision admin). */
export async function getAdminAdCampaigns(params = {}) {
  const query = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      query.set(key, String(value));
    }
  });
  const qs = query.toString();
  const response = await apiRequest(`/admin/campagnes-publicitaires${qs ? `?${qs}` : ''}`, {
    method: 'GET',
    withAuth: true,
  });
  return response.data;
}

export async function getAdminCampaignDistrictPolygons() {
  const response = await apiRequest('/admin/campagnes-publicitaires/districts-polygons', {
    method: 'GET',
    withAuth: true,
  });
  return Array.isArray(response.data) ? response.data : [];
}

export async function replaceAdminCampaignDistrictPolygons(
  districts: Array<{
    nom: string;
    district: 'LUKUNGA' | 'FUNA' | 'TSHANGU' | 'MONT_AMBA';
    geometry: unknown;
    poiCount?: number;
  }>,
) {
  const response = await apiRequest('/admin/campagnes-publicitaires/districts-polygons', {
    method: 'PUT',
    withAuth: true,
    body: JSON.stringify({ districts }),
  });
  return Array.isArray(response.data) ? response.data : [];
}

export async function syncAdminCampaignDistrictPolygons(payload: {
  create: Array<{
    nom: string;
    district: 'LUKUNGA' | 'FUNA' | 'TSHANGU' | 'MONT_AMBA';
    geometry: unknown;
    poiCount?: number;
  }>;
  update: Array<{
    id: string;
    nom: string;
    district: 'LUKUNGA' | 'FUNA' | 'TSHANGU' | 'MONT_AMBA';
    geometry: unknown;
    poiCount?: number;
  }>;
  delete: string[];
}) {
  const response = await apiRequest('/admin/campagnes-publicitaires/districts-polygons', {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return Array.isArray(response.data) ? response.data : [];
}
